import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { components } from '@bookingdei/api-client/types';
import { api } from '../api';
import { useLanguage } from './language-context';
import { useResource } from './use-resource';
import { AccountMenu } from './account-menu';
import { BrandLockup } from './brand-lockup';
import { ChromeMenu } from './chrome-menu';
import { ChromeMobileMenu } from './chrome-mobile-menu';
import { buildChromeNav } from './chrome-nav';
import { ScIcon } from './chrome-icons';
import { ThemeSwitch } from './theme-switch';
import { useSignedInAccount } from './signed-in-account';

type CategoryList = components['schemas']['CategoryList'];

// The site header every website page shares (Blog design export, owner's call 4 Nov 2026).
//
// Wide (> 1040 px): lockup · Offers (the "hot" item with the travelling ring) · Booking ·
// Videos NEW · Resources · divider · theme switch · the split pill "Sign in | Add your
// business FREE". Offers and Booking open the three-column mega panel, Resources a short
// list (chrome-menu.tsx). Signed in, the pill's left half is the account menu.
// Narrow (≤ 1040 px): lockup · theme switch · ☰, which opens the accordion menu under the
// bar (chrome-mobile-menu.tsx). The bar is sticky and never hides.
//
// The menus' Categories follow the registry (FR-L6); everything else is fixed copy. The
// "Add your business" half is left out on /for-business, where it would link to itself.
export function AppBar() {
  const { t, lang } = useLanguage();
  const { pathname, search } = useLocation();
  const { user } = useSignedInAccount();
  const [menuOpen, setMenuOpen] = useState(false);

  const categories = useResource<CategoryList>((signal) => api.GET('/registries/categories', { signal, lang }), [lang]);
  const nav = buildChromeNav(pathname, categories.data?.items, lang, t);

  // Any navigation closes the phone menu; Escape closes it too.
  useEffect(() => setMenuOpen(false), [pathname, search]);
  useEffect(() => {
    if (!menuOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const onForBusiness = /^\/for-business\/?$/.test(pathname);

  return (
    <header className="sc-hd">
      <div className="sc-in sc-hrow">
        <BrandLockup className="sc-logo" />
        <nav className="sc-nav" aria-label={t('nav.primary')}>
          <ChromeMenu menu={nav.offers} hot />
          <ChromeMenu menu={nav.booking} />
          <Link className="sc-nli" to="/videos" aria-current={nav.videosActive ? 'page' : undefined}>
            <ScIcon id="sc-i-video" className="sc-ni" />
            <span>{t('nav.videos')}</span>
            <span className="sc-bdg sc-bdg--new">{t('nav.new_badge')}</span>
          </Link>
          <ChromeMenu menu={nav.resources} />
        </nav>
        <div className="sc-hr">
          <span className="sc-sep" aria-hidden="true" />
          <ThemeSwitch />
          <div className="sc-split">
            {user ? (
              <span className="sc-split__acct">
                <AccountMenu />
              </span>
            ) : (
              <Link className="sc-split__l" to="/login">
                <ScIcon id="sc-i-user" />
                {t('nav.sign_in')}
              </Link>
            )}
            {!onForBusiness && (
              <Link className="sc-split__r" to="/for-business">
                {t('nav.add_business')}
                <b className="sc-free">{t('nav.free')}</b>
              </Link>
            )}
          </div>
          <button
            type="button"
            className="sc-burger"
            aria-label={t('nav.menu')}
            aria-expanded={menuOpen}
            aria-controls="sc-mnav"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <ScIcon id="sc-i-menu" />
          </button>
        </div>
      </div>
      <ChromeMobileMenu id="sc-mnav" open={menuOpen} menus={[nav.offers, nav.booking, nav.resources]} videosActive={nav.videosActive} />
    </header>
  );
}
